import {Fragment, useEffect, useState} from "react";
import {Button, IconButton, Table, TableBody, TableCell, TableRow, Tooltip} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormattedTableHead from "../../../FormattedTableHead";
import {defineMessage, FormattedMessage} from "react-intl";
import FormattedRegistrationTime from "../../../FormattedRegistrationTime";
import DeleteButton from "./DeleteButton";
import UpdateButton from "./save/UpdateButton";
import NoDeviceBlock from "../NoDeviceBlock";
import {HasPermission} from "../../../widgets";
import {ClientDevicesQueryHookResult, useSaveClientDeviceMutation} from "../queries.generated";
import {ClientType} from "@/api";
import {CLIENT_TYPE_MESSAGES} from "@/utils/translations";
import {
    clearLocalPhoneClient,
    getLocalPhoneClient,
    LocalPhoneClient,
    updateLocalPhoneClient,
} from "@/phone/localPhoneClient";

const DEFAULT_BROWSER_CLIENT_DESCRIPTION = "Browser client";

type NavigatorWithUserAgentData = Navigator & {
    userAgentData?: {
        brands?: Array<{brand: string; version: string}>;
        platform?: string;
    };
};

type Props = {
    query: ClientDevicesQueryHookResult;
    onMutate: () => void;
    showSetupBrowserClientButton?: boolean;
    canAddDesktopClient: boolean;
    extensionId: number;
    extensionIdentifier?: string;
    extensionDomainName?: string;
};

function getBrowserName(userAgent: string, brands?: Array<{brand: string; version: string}>) {
    const brand = brands?.find(({brand}) => !brand.includes("Brand") && !brand.includes("Chromium"));

    if (brand) return brand.brand;
    if (userAgent.includes("Firefox/")) return "Firefox";
    if (userAgent.includes("Edg/")) return "Edge";
    if (userAgent.includes("Chrome/")) return "Chrome";
    if (userAgent.includes("Safari/")) return "Safari";

    return null;
}

function getPlatformName(userAgent: string, platform?: string) {
    if (platform) return platform;
    if (userAgent.includes("Windows")) return "Windows";
    if (userAgent.includes("Android")) return "Android";
    if (userAgent.includes("iPhone") || userAgent.includes("iPad")) return "iOS";
    if (userAgent.includes("Mac OS X")) return "macOS";
    if (userAgent.includes("Linux")) return "Linux";

    return null;
}

function getBrowserClientDescription() {
    const userAgent = navigator.userAgent;
    const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;
    const browser = getBrowserName(userAgent, userAgentData?.brands);
    const platform = getPlatformName(userAgent, userAgentData?.platform);

    if (browser && platform) return `${browser} on ${platform}`;
    if (browser) return browser;
    if (platform) return `${DEFAULT_BROWSER_CLIENT_DESCRIPTION} on ${platform}`;

    return DEFAULT_BROWSER_CLIENT_DESCRIPTION;
}

const SetupBrowserClientButton = ({
    disabled,
    extensionId,
    extensionIdentifier,
    extensionDomainName,
    onSuccess,
    onLocalPhoneClientUpdate,
}: {
    disabled: boolean;
    extensionId: number;
    extensionIdentifier?: string;
    extensionDomainName?: string;
    onSuccess: () => ReturnType<ClientDevicesQueryHookResult["refetch"]>;
    onLocalPhoneClientUpdate: (localPhoneClient: LocalPhoneClient | null) => void;
}) => {
    const [saveClientDevice, {loading}] = useSaveClientDeviceMutation();
    const [isProcessing, setIsProcessing] = useState(false);

    async function setupBrowserClient() {
        setIsProcessing(true);

        try {
            const result = await saveClientDevice({
                variables: {
                    userId: extensionId,
                    clientType: ClientType.Desktop,
                    description: getBrowserClientDescription(),
                    clientInfo: navigator.userAgent,
                },
            });
            const saveResult = result.data?.saveClientDevice;
            const id = saveResult?.id;

            if (id == null || saveResult.errors?.length) {
                return;
            }

            updateLocalPhoneClient(extensionId, {id, extensionId});

            const refetchResult = await onSuccess();
            const device = refetchResult.data.clientDevices.find((device) => device.id === id);

            if (device) {
                onLocalPhoneClientUpdate(
                    updateLocalPhoneClient(extensionId, {
                        id,
                        extensionId,
                        identifier: extensionIdentifier,
                        domainName: extensionDomainName,
                        sipUser: device.sipUser,
                        sipPassword: device.sipPassword,
                    }),
                );
            } else {
                clearLocalPhoneClient(extensionId);
                onLocalPhoneClientUpdate(null);
            }
        } finally {
            setIsProcessing(false);
        }
    }

    return (
        <Button
            color="primary"
            disabled={disabled || loading || isProcessing}
            size="small"
            onClick={setupBrowserClient}
            variant="contained"
            id="setup-browser-client"
        >
            <FormattedMessage id="devices.client.setupBrowserClient" defaultMessage="Set up browser client" />
        </Button>
    );
};

const Client = ({
    query,
    onMutate,
    showSetupBrowserClientButton = false,
    canAddDesktopClient,
    extensionId,
    extensionIdentifier,
    extensionDomainName,
}: Props) => {
    const componentId = "extension-client-devices";
    const [localPhoneClient, setLocalPhoneClient] = useState<LocalPhoneClient | null>(() =>
        getLocalPhoneClient(extensionId),
    );
    const setupBrowserClientDisabled =
        !canAddDesktopClient || localPhoneClient !== null || !extensionIdentifier || !extensionDomainName;

    useEffect(() => {
        if (localPhoneClient === null) {
            return;
        }

        const hasMatchingDevice =
            localPhoneClient.id != null && query.data.clientDevices.some((device) => device.id === localPhoneClient.id);

        if (hasMatchingDevice) {
            return;
        }

        clearLocalPhoneClient(extensionId);
        setLocalPhoneClient(null);
    }, [extensionId, localPhoneClient, query.data.clientDevices]);

    async function onSuccess() {
        const refetchResult = await query.refetch();
        onMutate();
        return refetchResult;
    }

    function launchPhoneClient() {
        window.open(`/phone/${extensionId}`, "_blank", "noopener");
    }

    if (!query.data.clientDevices.length) {
        return (
            <div id={componentId}>
                <NoDeviceBlock idSuffix="client">
                    {showSetupBrowserClientButton && (
                        <SetupBrowserClientButton
                            disabled={setupBrowserClientDisabled}
                            extensionId={extensionId}
                            extensionIdentifier={extensionIdentifier}
                            extensionDomainName={extensionDomainName}
                            onSuccess={onSuccess}
                            onLocalPhoneClientUpdate={setLocalPhoneClient}
                        />
                    )}
                </NoDeviceBlock>
            </div>
        );
    }

    return (
        <Fragment>
            <Table id={componentId}>
                <FormattedTableHead
                    cells={[
                        {label: defineMessage({id: "description", defaultMessage: "Description"})},
                        {label: defineMessage({id: "type", defaultMessage: "Type"})},
                        {label: defineMessage({id: "devices.registrationTime", defaultMessage: "Registered"})},
                        {
                            props: {align: "center"},
                            renderer: () =>
                                showSetupBrowserClientButton ? (
                                    <SetupBrowserClientButton
                                        disabled={setupBrowserClientDisabled}
                                        extensionId={extensionId}
                                        extensionIdentifier={extensionIdentifier}
                                        extensionDomainName={extensionDomainName}
                                        onSuccess={onSuccess}
                                        onLocalPhoneClientUpdate={setLocalPhoneClient}
                                    />
                                ) : null,
                        },
                    ]}
                />
                <TableBody>
                    {query.data.clientDevices.map((device) => (
                        <TableRow key={device.id}>
                            <TableCell data-path="client-devices.description" data-value={device.description}>
                                {device.description}
                            </TableCell>
                            <TableCell>
                                <FormattedMessage {...CLIENT_TYPE_MESSAGES[device.type as ClientType]} />
                            </TableCell>
                            <TableCell>
                                <FormattedRegistrationTime
                                    registrationTime={device.registrationTime}
                                    expirationTime={device.registrationExpireTime}
                                    clientInfo={device.clientInfo}
                                />
                            </TableCell>
                            <TableCell align="center">
                                <HasPermission name="edit_admin_extension">
                                    <UpdateButton onSuccess={query.refetch} device={device} />
                                </HasPermission>
                                {localPhoneClient?.id === device.id && (
                                    <Tooltip
                                        title={
                                            <FormattedMessage
                                                id="devices.client.launchPhoneClient"
                                                defaultMessage="Launch phone client"
                                            />
                                        }
                                    >
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={launchPhoneClient}
                                            aria-label="Launch phone client"
                                        >
                                            <FontAwesomeIcon icon="phone" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <DeleteButton
                                    id={device.id}
                                    name={device.description}
                                    onSuccess={onSuccess}
                                    dataIdentifier={device.description}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Fragment>
    );
};

export default Client;
